/*
 * The corridor's points on WebGL: a couple of draw calls a frame for all of them, with the
 * projection, fades, sizes and highlight colours the 2D fallback in AnalysisScene uses. A
 * 2D canvas takes one call per point, which kept the page's scrolling near 20 fps.
 */
import { STRIDE } from "./world.js";

const VERTEX = `
attribute vec3 aPos;
attribute float aIntensity;
attribute float aHi;
attribute float aAlong;
uniform vec3 uCam;
uniform vec3 uF;
uniform vec3 uR;
uniform vec3 uU;
uniform vec2 uCenter;
uniform vec2 uView;
uniform float uFocal;
uniform float uFar;
uniform float uReveal;
uniform float uReach;
uniform float uDim;
uniform float uSizeK;
uniform float uDpr;
uniform float uPass;
uniform vec3 uColors[12];
varying vec4 vColor;

void main() {
  vec3 v = aPos - uCam;
  float zc = dot(v, uF);
  bool lit = aHi > 0.5 && aAlong <= uReach;
  float fade = min(1.0, zc / 2.5) * min(1.0, (uFar - zc) / (uFar * 0.45));
  float front = uReveal - zc;
  if (front < 20.0) fade = min(1.0, fade + (1.0 - front / 20.0) * 0.5);
  // Pass 0 draws the grey points, pass 1 the highlights over them.
  if (zc < 0.6 || zc > uFar || zc > uReveal || fade < 0.06 || lit != (uPass > 0.5)) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    return;
  }
  float sx = uCenter.x + dot(v, uR) / zc * uFocal;
  float sy = uCenter.y - dot(v, uU) / zc * uFocal;
  gl_Position = vec4(sx / uView.x * 2.0 - 1.0, 1.0 - sy / uView.y * 2.0, 0.0, 1.0);
  float level = aIntensity * uDim;
  vec3 colour = uColors[0];
  if (lit) {
    level = 0.5 + aIntensity * 0.5;
    fade = min(1.0, fade * 1.25);
    colour = uColors[int(aHi + 0.5)];
  }
  // The 2D canvas's steps: 12 brightness levels, 8 opacities.
  float k = 0.22 + 0.78 * floor(level * 11.0 + 0.5) / 11.0;
  float alpha = (floor(fade * 7.0 + 0.5) + 1.0) / 8.0;
  vColor = vec4(colour * k * alpha, alpha);
  gl_PointSize = (clamp(uSizeK / zc, 1.2, 2.6) + (lit ? 0.3 : 0.0)) * uDpr;
}
`;

const FRAGMENT = `
precision mediump float;
varying vec4 vColor;
void main() {
  gl_FragColor = vColor;
}
`;

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
}

/**
 * Sets up `canvas` for the world's points. `colors` are 12 [r, g, b] (0-255): the grey
 * points' first, then the highlights' by index. Returns null without WebGL.
 */
export function createPoints(canvas, world, colors) {
  let gl = null;
  try {
    gl = canvas.getContext("webgl", {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
    });
  } catch {
    gl = null;
  }
  if (!gl) return null;
  const vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
  if (!vs || !fs) return null;
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  const { points: P, count } = world;
  const pos = new Float32Array(count * 3);
  const intensity = new Float32Array(count);
  for (let j = 0; j < count; j++) {
    const i = j * STRIDE;
    pos[j * 3] = P[i];
    pos[j * 3 + 1] = P[i + 1];
    pos[j * 3 + 2] = P[i + 2];
    intensity[j] = P[i + 3];
  }
  const attribute = (name, data, size, type = gl.FLOAT) => {
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(program, name);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, type, false, 0, 0);
    return buffer;
  };
  attribute("aPos", pos, 3);
  attribute("aIntensity", intensity, 1);
  const hiBuffer = attribute("aHi", new Uint8Array(count), 1, gl.UNSIGNED_BYTE);
  const alongBuffer = attribute("aAlong", new Float32Array(count), 1);

  const u = {};
  for (const name of [
    "uCam",
    "uF",
    "uR",
    "uU",
    "uCenter",
    "uView",
    "uFocal",
    "uFar",
    "uReveal",
    "uReach",
    "uDim",
    "uSizeK",
    "uDpr",
    "uPass",
    "uColors",
  ]) {
    u[name] = gl.getUniformLocation(program, name);
  }
  gl.uniform3fv(u.uColors, new Float32Array(colors.flatMap((c) => c.map((x) => x / 255))));
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(0, 0, 0, 0);

  let shownLights = null;
  return {
    lost: () => gl.isContextLost(),
    /** Draws one frame; `f` carries the camera, framing and the analysis's `lights`. */
    draw(f) {
      if (f.lights !== shownLights) {
        shownLights = f.lights;
        gl.bindBuffer(gl.ARRAY_BUFFER, hiBuffer);
        gl.bufferData(
          gl.ARRAY_BUFFER,
          f.lights ? f.lights.hi : new Uint8Array(count),
          gl.STATIC_DRAW
        );
        gl.bindBuffer(gl.ARRAY_BUFFER, alongBuffer);
        gl.bufferData(
          gl.ARRAY_BUFFER,
          f.lights ? f.lights.along : new Float32Array(count),
          gl.STATIC_DRAW
        );
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform3fv(u.uCam, f.cam);
      gl.uniform3fv(u.uF, f.forward);
      gl.uniform3fv(u.uR, f.right);
      gl.uniform3fv(u.uU, f.up);
      gl.uniform2f(u.uCenter, f.cx, f.cy);
      gl.uniform2f(u.uView, f.w, f.h);
      gl.uniform1f(u.uFocal, f.focal);
      gl.uniform1f(u.uFar, f.far);
      // Shaders take no Infinity: a million metres is past everything.
      gl.uniform1f(u.uReveal, Math.min(f.reveal, 1e6));
      gl.uniform1f(u.uReach, Math.min(f.reach, 1e6));
      gl.uniform1f(u.uDim, f.dim);
      gl.uniform1f(u.uSizeK, f.sizeK);
      gl.uniform1f(u.uDpr, f.dpr);
      for (const pass of [0, 1]) {
        gl.uniform1f(u.uPass, pass);
        gl.drawArrays(gl.POINTS, f.first, f.last - f.first);
      }
    },
  };
}
